import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import FJButton, { FJButtonType } from '../../../../common/components/FJButton/FJButton';
import FJImage from '../../../../common/components/FJImage/FJImage';
import FJLocalStore from '../../../../store/FJLocalStore';
import FJBackIcon from '../../../../assets/svg/back-arrow.svg?react';
import FJDeleteWhiteIcon from '../../../../assets/svg/delete-white.svg?react';
import FJDownloadIcon from '../../../../assets/svg/download.svg?react';
import FJVerticalScroller from '../../../../common/components/FJScroller/FJVerticalScroller';
import FJTextWithEllipsisParent from '../../../../common/components/FJTextWithEllipsis/FJTextWithEllipsisParent';
import FJSelectAll from '../../../../common/components/FJSelectAll/FJSelectAll';
import FJCheckBox from '../../../../common/components/FJCheckBox/FJCheckBox';
import FJAppConstant from '../../../../constants/FJAppConstant';

import styles from './FJHeadshotPreview.module.less';
import FJToolTip from '../../../../common/components/FJToolTip/FJToolTip';

function FJHeadshotPreview({
    currentCollection,
    onGoBack,
    onRecreate,
    onDownload,
    onDelete,
    selectList,
    isMultipleMode,
    setSelectList,
    multiDelete,
    clearSelected,
    multiDownload,
    onImagePreview,
    onRetry,
    tabIndex,
    id,
}) {
    const handleSelect = useCallback(
        (item, isSelected) => {
            if (!isMultipleMode) return;

            if (isSelected) {
                setSelectList(selectList.filter(id => id !== item.id));
            } else {
                setSelectList([...selectList, item.id]);
            }
        },
        [selectList, setSelectList, isMultipleMode],
    );

    const renderImage = item => {
        const isSelected = selectList?.includes(item.id);
        const status = item.status;
        switch (status) {
            case 'processing':
                return (
                    <div className={styles.headshot_item} key={item.id}>
                        <div
                            className={classNames(styles.aspect_ratio_box, isMultipleMode && styles.active)}
                            style={{ aspectRatio: item.width / item.height }}
                            onClick={() => {
                                handleSelect(item, isSelected);
                            }}
                        >
                            <FJImage
                                imgClassName={styles.headshot_item_image}
                                boxClassName={styles.headshot_item_box}
                                loadingClassName={styles.headshot_item_image}
                                isLoading
                            />
                        </div>
                    </div>
                );
            case 'failed':
                return (
                    <div className={styles.headshot_item} key={item.id}>
                        <div className={classNames(styles.selection_checkbox, isMultipleMode && styles.active)}>
                            <FJCheckBox
                                selected={isSelected}
                                dataChange={() => {
                                    if (isSelected) {
                                        setSelectList(selectList.filter(id => id !== item.id));
                                    } else {
                                        setSelectList([...selectList, item.id]);
                                    }
                                }}
                            />
                        </div>
                        <div
                            className={classNames(
                                styles.aspect_ratio_box,
                                isMultipleMode && styles.active,
                                styles.failed_box,
                            )}
                            style={{ aspectRatio: item.width / item.height }}
                            onClick={() => {
                                if (isMultipleMode) {
                                    handleSelect(item, isSelected);
                                } else {
                                    onImagePreview?.(item.id);
                                }
                            }}
                        >
                            <div
                                className={styles.image_actions}
                                style={{ visibility: isMultipleMode ? 'hidden' : 'visible' }}
                            >
                                {
                                    <>
                                        <div
                                            className={styles.delete_button}
                                            onClick={e => {
                                                e.stopPropagation();
                                                onDelete([item.id]);
                                            }}
                                        >
                                            <FJDeleteWhiteIcon />
                                        </div>
                                    </>
                                }
                            </div>
                            <div
                                className={styles.retry_button}
                                onClick={e => {
                                    e.stopPropagation();
                                    onRetry(item.eventId, item.id);
                                }}
                            >
                                {FJLocalStore._('retry')}
                            </div>
                        </div>
                    </div>
                );
            case 'succeeded':
            default:
                return (
                    <div className={styles.headshot_item} key={item.id}>
                        <div className={classNames(styles.selection_checkbox, isMultipleMode && styles.active)}>
                            <FJCheckBox
                                selected={isSelected}
                                dataChange={() => {
                                    if (isSelected) {
                                        setSelectList(selectList.filter(id => id !== item.id));
                                    } else {
                                        setSelectList([...selectList, item.id]);
                                    }
                                }}
                            />
                        </div>
                        <div
                            className={styles.image_actions}
                            style={{ visibility: isMultipleMode ? 'hidden' : 'visible' }}
                        >
                            <FJToolTip title={FJLocalStore._('delete')}>
                                <div
                                    className={styles.delete_button}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onDelete([item.id]);
                                    }}
                                >
                                    <FJDeleteWhiteIcon />
                                </div>
                            </FJToolTip>
                            <FJToolTip title={FJLocalStore._('download')}>
                                <div
                                    className={styles.download_button}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onDownload([item.id]);
                                    }}
                                >
                                    <FJDownloadIcon />
                                </div>
                            </FJToolTip>
                        </div>
                        <div
                            className={classNames(styles.aspect_ratio_box, isMultipleMode && styles.active)}
                            style={{ aspectRatio: item.width / item.height }}
                            onClick={() => {
                                if (isMultipleMode) {
                                    handleSelect(item, isSelected);
                                } else {
                                    onImagePreview?.(item.id);
                                }
                            }}
                        >
                            <FJImage
                                imgSrc={item.imgUrl}
                                imgClassName={styles.headshot_item_image}
                                boxClassName={styles.headshot_item_box}
                                isShowLoading
                            />
                        </div>
                    </div>
                );
        }
    };

    const isShow = useMemo(() => {
        return currentCollection && currentCollection.headshotList.length > 0 && tabIndex === 1;
    }, [currentCollection, tabIndex]);

    return (
        <div className={classNames(styles.preview_container, { [styles.show]: isShow })} key={id} id={id}>
            <div className={styles.header}>
                <div className={styles.back_button} onClick={onGoBack}>
                    <FJBackIcon className={styles.back_icon} />
                    {FJLocalStore._('back')}
                </div>
                <FJTextWithEllipsisParent text={currentCollection?.name} className={styles.title} />
                <FJButton
                    type={FJButtonType.GRADIENT}
                    className={styles.recreate_button}
                    onClick={() => onRecreate(currentCollection?.category)}
                    text={FJLocalStore._('recreate')}
                />
            </div>
            <FJVerticalScroller className={styles.images_scroller}>
                <div className={styles.images_container}>
                    {currentCollection?.headshotList?.map(item => {
                        return renderImage(item);
                    })}
                </div>
            </FJVerticalScroller>

            <FJSelectAll
                appType={FJAppConstant.HEADSHOT_GENERATOR}
                list={currentCollection?.headshotList ?? []}
                selectList={selectList}
                setSelectList={setSelectList}
                multiDelete={multiDelete}
                isMultipleMode={isMultipleMode}
                clearSelected={clearSelected}
                isShowAddToMediaButton={false}
                multiDownload={multiDownload}
            />
        </div>
    );
}

FJHeadshotPreview.propTypes = {
    currentCollection: PropTypes.object,
    onGoBack: PropTypes.func,
    onRecreate: PropTypes.func,
    onDownload: PropTypes.func,
    onDelete: PropTypes.func,
    selectList: PropTypes.array,
    isMultipleMode: PropTypes.bool,
    setSelectList: PropTypes.func,
    multiDelete: PropTypes.func,
    clearSelected: PropTypes.func,
    multiDownload: PropTypes.func,
    onImagePreview: PropTypes.func,
    onRetry: PropTypes.func,
    tabIndex: PropTypes.number,
    id: PropTypes.string,
};

export default FJHeadshotPreview;
